<template>
  <div id="view-all-component">
    <sticky>
      <div class="clearfix header-panel">
        <div class="header">
          <span class="notice-header">
            {{ $i18n.t( 'project.viewAll.title') }}
          </span>
        </div>
        <div class="right-btns">
          <CreateProject
            v-if="showCreateButton"
            class="create-btn"
          />
          <HKLButton
            v-if="canExportSearchResults"
            color="orange"
            @click="exportSearchResults"
          >
            {{ $t('action.exportSearchResults') }}
          </HKLButton>
          <button
            type="button"
            class="btn btn--darkblue"
            @click="resetFilters()"
          >
            {{ $t('action.resetFilter') }}
          </button>
          <button
            id="query-btn"
            type="button"
            class="btn btn--orange"
            @click="queryProjects()"
          >
            {{ $t('action.search') }}
          </button>
        </div>
      </div>
    </sticky>

    <div class="hkld-project-table-container">
      <div>
        <span class="total">{{ $t('total') }}: {{ displayTotalCount }}</span>
      </div>
      <CommonTable
        ref="hkld-project-table"
        tableId="hkld-project-table"
        :renderHeaderList="itemHeaders"
        :tableContentLists="parsedItems"
        :needSearch="true"
        :defaultOrder="true"
        :yScrollAuto="true"
        :getDisplayListLength="getDisplayListLength"
        :defaultOrderKey="defaultOrderKey"
        :orderByDateKeys="['completedDate', 'createdAt']"
        :orderByNumberKeys="['completionPercentage']"
        :interactionFunc="interactionFunc"
        :getConditions="getConditionsCallback"
        :enterEvent="queryProjects"
        :getTableOrder="getTableOrder"
      />
    </div>
    <HKLRemindModal ref="remindEmptyModal">
      <template slot="title">
        {{ $t('hint.emptySearch') }}
      </template>
    </HKLRemindModal>
  </div>
</template>

<script>
import currencyFormatter from 'currency-formatter'
import lodash from 'lodash'
import { ref, computed, getCurrentInstance } from 'vue-demi'
import { mapActions, mapGetters } from 'vuex'
import { exportAllProjects, listAllProjects } from '@/api/projects/request'
import { getPurchase, getPurchaseNumber } from '@/api/projects/util'
import HKLButton from '@/controls/Button/HKLButton'
import CommonTable from '@/controls/CommonTable'
import HKLInputOperatorNumber from '@/controls/Input/HKLInputOperatorNumber'
import HKLMultiSelect, { ALL_VALUE } from '@/controls/Select/HKLMultiSelect'
import Sticky from '@/controls/Sticky'
import { INPUT_TYPE } from '@/modules/contacts/searchFilter.const'
import CreateProject from '@/modules/projects/CreateProject'
import { DATA_CACHE_KEYS } from '@/store/util'
import { aclHelper, aclPermsType } from '@/utils/aclHelper'
import { projectRouteNames } from '@/utils/consts'
import { LIST_NAV_KIND } from '@/constants/listNavigation'
import mutationTypes from '@/store/mutation-types'
import dateUtil from '@/utils/dateUtil'
import { projectStatus } from '@/utils/locale'
import { projectNavToDetailPage } from '@/utils/navigation'
import userHelper from '@/utils/userHelper'
import HKLRemindModal from '@/controls/Modal/HKLRemindModal.vue'
import { useRefModal } from '@/controls/Modal/modal.hook'
import viewAllCommonTableSnapshotMixin from '@/mixins/viewAllCommonTableSnapshot.mixin'
import { consumeResumeSearchQueryOnce, ENGINEER_PROJECTS_VIEW_ALL_LEAVE_PRESERVE_ROUTE_NAMES } from '@/constants/viewAllTableCache'
import { useUserSiteOptions } from '../hook'

const sortingAttributes = {
  completionInfo: {
    completedDate: {
      gte: null,
      lte: null,
    },
  },
  poInfo: {
    amount: null,
    poDescription: null,
    site: [],
  },
  projectInfo: {
    status: [],
    number: null,
    completedDate: null,
    manager: null,
    vendor: null,
  },
  local: {
    completionRange: null,
  },
}

export default {
  name: 'ProjectViewAll',
  /** @see `@/mixins/viewAllCommonTableSnapshot.mixin.js` */
  viewAllTableSnapshotId: 'hkld-project-table',
  viewAllTableLeavePreserveRouteNames: ENGINEER_PROJECTS_VIEW_ALL_LEAVE_PRESERVE_ROUTE_NAMES,
  components: {
    HKLButton,
    Sticky,
    CommonTable,
    CreateProject,
    HKLRemindModal,
  },
  mixins: [viewAllCommonTableSnapshotMixin],
  setup () {
    const { proxy: that } = getCurrentInstance()
    let returnData = {
      canExportSearchResults: computed(() => aclHelper.getAccess(aclPermsType.projects.exportViewAll)),
    }
    const defaultOrderKey = 'completedDate'
    const orderKey = ref(defaultOrderKey)
    const reverse = ref(true)
    const getTableOrder = (key, order) => {
      orderKey.value = key
      reverse.value = order
    }
    const exportSearchResults = () => {
      return exportAllProjects({
        diskCacheKey: DATA_CACHE_KEYS.VIEW_ALL_PROJECT,
        orderInfo: {
          orderbyAttr: orderKey.value,
          reverse: Number(reverse.value),
        },
      })
    }
    const { data: siteOptions } = useUserSiteOptions(projectRouteNames.projects)
    const checkIsEmptyFilter = () => {
      const obj = lodash.fromPairs(
        that.itemHeaders.map((itemHeader) => [itemHeader.name, lodash.get(that.sortingAttributes, itemHeader.queryName)]),
      )
      that.itemHeaders.forEach((itemHeader) => {
        if (itemHeader.inputType !== INPUT_TYPE.DATE) return
        const key = itemHeader.name
        lodash.set(obj, key, lodash.get(obj, `${key}.gte`) || lodash.get(obj, `${key}.lte`))
      })

      const result = Object.values(obj).some((v) => {
        if (Array.isArray(v) || typeof v === 'string') return !!v.length
        return !!v
      })
      return !result
    }
    const { open: remindEmptyModalOpen, modal: remindEmptyModal } = useRefModal()
    returnData = Object.assign(returnData, {
      siteOptions,
      exportSearchResults,
      getTableOrder,
      defaultOrderKey,
      checkIsEmptyFilter,
      remindEmptyModalOpen,
      remindEmptyModal,
    })
    return returnData
  },
  data () {
    return {
      dateUtil,
      interactionFunc: {
        'number': this.navToPage,
        'projectTitle': this.navToPage,
      },
      sortingAttributes: lodash.cloneDeep(sortingAttributes),
      /** Row list only from latest API response (no Vuex `allLastSearchedProjects`). */
      projectRows: [],
      localLastSearchCount: 0,
      cachedDisplayListLength: 0,
      projectStatus,
    }
  },
  computed: {
    ...mapGetters('user', ['isContractor', 'currentUser']),
    ...mapGetters('currencies', { poCurrencies: 'po', pcCurrencies: 'pc' }),
    userHelper () {
      return userHelper
    },
    ...mapGetters('projects', ['lastSearchedAttributes']),
    displayTotalCount () {
      return this.localLastSearchCount || this.cachedDisplayListLength
    },
    showCreateButton () {
      return aclHelper.getAccess(aclPermsType.projects.manage)
    },
    parsedItems () {
      return (this.projectRows || []).map(project => {
        const purchase = getPurchase(project)
        const status = this.$t(`status.${project.status.toLowerCase()}`).toUpperCase()
        return {
          id: lodash.get(project, 'id'),
          number: getPurchaseNumber(lodash.get(project, 'numberType'), purchase),
          status,
          completedDate: lodash.get(project, 'completedDate'),
          createdAt: lodash.get(project, 'createdAt'),
          picName: userHelper.getUserName(
            lodash.get(project, 'manager.username'),
            lodash.get(project, 'manager.displayName')),
          vendor: lodash.get(project, 'vendorCompany.name'),
          managedBy: lodash.get(project, 'managedBy'),
          hasAccess: lodash.get(project, 'hasAccess'),
          completionPercentage: lodash.get(project, 'completionPercentage'),
          rawProject: project,
          allowLink: lodash.get(project, 'hasAccess'),
          projectTitle: lodash.get(purchase, 'description'),
          site: lodash.get(purchase, 'siteId'),
          amount: lodash.get(purchase, 'amount'),
          currencyCode: lodash.get(purchase, 'currencyCode'),
          dlpDate: lodash.get(project, 'dlpDate'),
        }
      })
    },
    itemHeaders () {
      return [
        {
          name: 'number',
          title: this.$t('projectViewAll.poContractNo'),
          inputType: INPUT_TYPE.INPUT,
          defaultValue: this.$t('emptyValue'),
          queryName: ['projectInfo', 'number'],
        },
        {
          name: 'projectTitle',
          title: this.$t('projectViewAll.projectTitle'),
          inputType: INPUT_TYPE.INPUT,
          defaultValue: this.$t('emptyValue'),
          pureText: true,
          queryName: ['poInfo', 'poDescription'],
        },
        {
          name: 'site',
          title: this.$t('projectViewAll.site'),
          defaultValue: this.$t('emptyValue'),
          defaultFilterValue: [],
          component: HKLMultiSelect,
          options: {
            attributes: {
              options: this.siteOptions || [],
            },
          },
          queryName: ['poInfo', 'site'],
        },
        {
          name: 'status',
          title: this.$t('projectViewAll.status'),
          component: HKLMultiSelect,
          defaultFilterValue: [],
          options: {
            attributes: {
              includeAll: true,
              options: Object.keys(projectStatus)
                .map(statusName => {
                  return {
                    name: this.$t(`status.${statusName.toLowerCase()}`).toUpperCase(),
                    value: statusName,
                  }
                }),
            },
          },
          queryName: ['projectInfo', 'status'],
        },
        {
          name: 'completionPercentage',
          title: this.$t('projectViewAll.completion'),
          component: HKLInputOperatorNumber,
          formatRule: this.addPercentSymbol,
          defaultValue: this.$t('emptyValue'),
          queryName: ['local', 'completionRange'],
          options: {
            listeners: {
              'keydown.Enter': this.queryProjects,
            },
          },
        },
        {
          name: 'completedDate',
          title: this.$t('projectViewAll.completionDate'),
          inputType: INPUT_TYPE.DATE,
          defaultValue: this.$t('emptyValue'),
          datePickerCanPutTotop: false,
          queryName: ['completionInfo', 'completedDate'],
          dateFromPlaceholder: this.$t('projectViewAll.startFrom'),
          dateToPlaceholder: this.$t('projectViewAll.until'),
        },
        {
          name: 'picName',
          title: this.$t('projectViewAll.personInCharge'),
          inputType: INPUT_TYPE.INPUT,
          defaultValue: this.$t('emptyValue'),
          queryName: ['projectInfo', 'manager'],
        },
        {
          name: 'amount',
          title: this.$t('poContractValue'),
          component: HKLInputOperatorNumber,
          defaultValue: this.$t('emptyValue'),
          formatRule: this.formatMoney,
          queryName: ['poInfo', 'amount'],
          options: {
            listeners: {
              'keydown.Enter': this.queryProjects,
            },
          },
        },
        {
          name: 'currencyCode',
          title: this.$t('currency'),
          component: HKLMultiSelect,
          defaultFilterValue: [],
          options: {
            attributes: {
              options: lodash.uniq([...this.poCurrencies, ...this.pcCurrencies].filter(Boolean)).sort(),
            },
          },
          queryName: ['poInfo', 'currencyCode'],
        },
        {
          name: 'vendor',
          title: this.$t('projectViewAll.vendor'),
          inputType: INPUT_TYPE.INPUT,
          queryName: ['projectInfo', 'vendor'],
        },
        {
          name: 'createdAt',
          title: this.$t('projectViewAll.createdDate'),
          inputType: INPUT_TYPE.DATE,
          defaultValue: this.$t('emptyValue'),
          datePickerCanPutTotop: false,
          queryName: ['projectInfo', 'createdAt'],
          dateFromPlaceholder: this.$t('projectViewAll.startFrom'),
          dateToPlaceholder: this.$t('projectViewAll.until'),
        },
        {
          name: 'dlpDate',
          title: this.$t('projectViewAll.dlpDate'),
          inputType: INPUT_TYPE.DATE,
          datePickerCanPutTotop: false,
          defaultValue: this.$t('emptyValue'),
          queryName: ['projectInfo', 'dlpDate'],
          dateFromPlaceholder: this.$t('projectViewAll.startFrom'),
          dateToPlaceholder: this.$t('projectViewAll.until'),
          disabledDateRangeBySelect: true,
          formatRule: val => (dateUtil.format(val, 'DD MMM YY') || this.$t('emptyValue')),
        },
      ]
    },
  },
  async mounted () {
    const { didStripResumeSearch } = await consumeResumeSearchQueryOnce(
      this.$router,
      this.$route.query,
      projectRouteNames.projects,
    )
    if (didStripResumeSearch && !lodash.isEmpty(this.lastSearchedAttributes)) {
      this.sortingAttributes = lodash.cloneDeep(this.lastSearchedAttributes)
    }
    if (didStripResumeSearch && !this.checkIsEmptyFilter()) {
      await this.queryProjects()
    }
  },
  methods: {
    ...mapActions('projects', ['setLastSearchedAttributes']),
    getProjectListNavigationEntries () {
      const table = this.$refs['hkld-project-table']
      const rows = table && Array.isArray(table.formatList) ? table.formatList : this.parsedItems
      if (!rows || !rows.length) return []
      return rows
        .filter(row => row.allowLink !== false)
        .map(row => ({ id: row.id, kind: LIST_NAV_KIND.PROJECT }))
    },
    commitProjectListNavigationSequence () {
      const entries = this.getProjectListNavigationEntries()
      this.$store.commit(`listNavigation/${mutationTypes.LIST_NAVIGATION_SET_SEQUENCE}`, { entries })
    },
    navToPage (item) {
      if (!this.isContractor) {
        this.commitProjectListNavigationSequence()
      }
      projectNavToDetailPage(item.id, this.isContractor, this.$router.currentRoute.name)
    },
    resetFilters () {
      this.sortingAttributes = lodash.cloneDeep(sortingAttributes)
      this.setLastSearchedAttributes({})
      this.$nextTick(() => {
        const table = this.$refs['hkld-project-table']
        if (table) table.resetFilterUiOnly()
      })
    },
    navBack () {
      this.$router.goBack()
    },
    async queryProjects () {
      if (this.checkIsEmptyFilter()) return this.remindEmptyModalOpen()
      this.setLastSearchedAttributes(this.sortingAttributes)
      try {
        const loadingLayer = lodash.get(this.$root.$children[0], '$refs.loadingLayer')
        loadingLayer && loadingLayer.$emit('startLoading')
        this.projectRows = []
        this.localLastSearchCount = 0
        const [projects, lastSearchCount] = await listAllProjects(
          this.getParsedAttributes(),
          DATA_CACHE_KEYS.VIEW_ALL_PROJECT,
        )
        this.projectRows = Array.isArray(projects) ? projects : []
        this.localLastSearchCount = typeof lastSearchCount === 'number' ? lastSearchCount : (this.projectRows.length || 0)
      } finally {
        const loadingLayer = lodash.get(this.$root.$children[0], '$refs.loadingLayer')
        loadingLayer && loadingLayer.$emit('stopLoading')
      }
    },
    getDisplayListLength (length) {
      this.cachedDisplayListLength = length
    },
    getParsedAttributes () {
      const sortingAttributes = lodash.cloneDeep(this.sortingAttributes)
      if (sortingAttributes.projectInfo.status.includes(ALL_VALUE)) {
        sortingAttributes.projectInfo.status = []
      }
      return sortingAttributes
    },
    formatMoney (value) {
      return currencyFormatter.format(value, 'USD')
    },
    addPercentSymbol (value) {
      return value ? value + '%' : (value === 0) ? 0 : this.$t('emptyValue')
    },
    getConditionsCallback (searchConditions) {
      const conditions = lodash.cloneDeep(searchConditions)
      this.itemHeaders.forEach(header => {
        const condition = conditions[header.name]
        if (condition) {
          const value = typeof condition.value === 'string' ? condition.value.trim() : condition.value
          switch (true) {
            case (header.inputType === INPUT_TYPE.DATE):
              lodash.set(this.sortingAttributes, [header.queryName[0], header.queryName[1], 'gte'], (value[0] || null))
              lodash.set(this.sortingAttributes, [header.queryName[0], header.queryName[1], 'lte'], dateUtil.getEndOfDay(value[1]))
              break
            case ['site', 'status'].includes(header.name):
              this.sortingAttributes[header.queryName[0]][header.queryName[1]] = value.filter(Boolean)
              break
            default:
              this.sortingAttributes[header.queryName[0]][header.queryName[1]] = value
              break
          }
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped src="styles/viewAll.scss"></style>
<style lang="scss" scoped src="styles/dashboard/style.scss"></style>
<style lang="scss" scoped src="styles/dashboard/datatable.scss"></style>
<style lang="scss">
#view-all-component {
  .inline-date-picker-block .vdp-datepicker {
    width: 130px!important;
    display: inline-block;
    min-height: 34px;
    vertical-align: bottom;
    input[type="text"] {
      margin: 0px;
    }
  }
}
.create-btn {
  display: inline-block;
}
.hkld-project-table-container {
  .total {
    margin: 20px 20px 0;
    display: inline-block;
  }
}
.hkld-project-table_common-table {
  height: calc(100vh - 239px);
  min-height: unset !important;
  margin: 0 20px !important;
  &.common-table .common-table-content-container {
      height: calc(100% - 134px);
      min-width: 2200px
    }
  .common-table-header-container {
    min-width: 2663px !important;
  }

  .vendor {
    min-width: 250px;
    max-width: 250px;
  }
  .projectTitle {
    width: calc(100% - 2350px);
  }
  .createdAt,
  .completedDate,
  .dlpDate {
    max-width: 310px;
    min-width: 310px;
  }
  .amount {
    max-width: 185px;
    min-width: 185px;
  }
  .site{
    max-width: 150px;
    min-width: 150px;
  }
  .currencyCode {
    max-width: 120px;
    min-width: 120px;
  }
  .number,
  .picName,
  .status {
    max-width: 180px;
    min-width: 180px;
  }
  .completionPercentage {
    max-width: 175px;
    min-width: 175px;
  }
  /* Restore filter-row column widths when CommonTable (PMS-157) sets .common-header-filter th { min-width: 0 }. */
  .common-header-filter {
    th.number,
    th.picName,
    th.status {
      min-width: 180px;
      max-width: 180px;
    }
    th.projectTitle {
      width: calc(100% - 2350px);
    }
    th.site {
      min-width: 150px;
      max-width: 150px;
    }
    th.completionPercentage {
      min-width: 175px;
      max-width: 175px;
    }
    th.completedDate,
    th.createdAt,
    th.dlpDate {
      min-width: 310px;
      max-width: 310px;
    }
    th.amount {
      min-width: 185px;
      max-width: 185px;
    }
    th.currencyCode {
      min-width: 120px;
      max-width: 120px;
    }
    th.vendor {
      min-width: 250px;
      max-width: 250px;
    }
  }
}
</style>
